import { withApollo } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { Type, read, write } from '@acorns/web-utils'
import * as Sentry from '@sentry/browser'
import { merge } from 'ramda'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import { APP_VERSION, AUTH_TOKEN, IS_RESUME_FLOW } from 'app-constants'
import { ActionType, FunnelName, PageName } from 'generated/types'
import { setCookies } from 'utils/auth'
import { withBackend } from 'utils/backend'
import { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getDefaultFlow } from 'utils/FlowProvider/utils'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { mocOnboardingEnabled, registrationServiceResumeFunnel } from 'utils/Optimizely/features'
import removeDuplicateQueryParams from 'utils/sourceAttributes'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import ResumeFlow from './ResumeFlow'
import userQuery from './user.query.gql'
import { didLogin, isNewTab } from './utils'

const unauthenticated = ctx => {
  return merge(ctx, { unauthenticated: true })
}

const fetchRefreshCookie = () => (ctx = {}) => {
  const token = read(Type.token)

  if (token) {
    write(Type.token, token)
    write(Type.partition, read(Type.partition))

    // Safari does not persist cookies between redirects.
    window.sessionStorage.setItem(AUTH_TOKEN, token)

    return Promise.resolve(merge(ctx, { refreshCookie: token }))
  } else {
    return Promise.resolve(unauthenticated(ctx))
  }
}

const fetchUser = ({
  client,
  isTieredRegEnabled,
  setRegistrationComplete,
  optimizely,
  registrationFunnel,
}) => (ctx: any = {}) => {
  if (ctx.unauthenticated) {
    return ctx
  }

  return client
    .query({ query: userQuery, fetchPolicy: 'network-only' })
    .then(({ data: { user, registration } }) => {
      let previousFlow

      if (!user) {
        return unauthenticated(ctx)
      }

      if (optimizely.isFeatureEnabled(registrationServiceResumeFunnel)) {
        registrationFunnel(user.uuid)
      } else {
        previousFlow = registration.funnel
      }

      if (isTieredRegEnabled && previousFlow === 'core') {
        previousFlow = getDefaultFlow()
      } else if (previousFlow === 'direct_to_mobile') {
        previousFlow = 'directToMobile'
      } else if (previousFlow === 'mightyOakCardOnboarding') {
        previousFlow = 'mightyOakCardOnboarding'
      }

      setRegistrationComplete(user.registrationComplete)

      return merge(ctx, {
        previousFlow,
        regComplete: user.registrationComplete,
        userUuid: user.uuid,
        userEmail: user.email,
      })
    })
}

const handleResumeCases = props => (ctx: any = {}) => {
  if (ctx.unauthenticated) {
    return props.handleSkipResume()
  }

  if (ctx.redirectToTierSelection && !ctx.regComplete && props.isTieredRegEnabled) {
    return props.handleRedirectToTierSelection()
  }

  if (ctx.regComplete) {
    return props.handleRegistrationComplete()
  }

  if (window.location.pathname !== FlowRoute.Success) {
    return props.handleResume(ctx)
  } else {
    props.setLoading(false)
  }
}

const handleSkipResume = ({ setLoading }) => () => {
  setResumeFlow(false)
  setLoading(false)
}

const handleRedirectToTierSelection = () => () => {
  const selectTierPathname = FlowRoute.SelectTier
  window.location.replace(`${selectTierPathname}`)
}

const handleRegistrationComplete = ({ env }) => () => {
  setResumeFlow(false)
  window.location.assign(env.get('webAppUrl'))
}

const handleResume = ({
  setShowWelcomeModal,
  backend,
  setFlow,
  tierPrice,
  metadata,
  optimizely,
  updateAttribution,
}) => ({ refreshCookie, userUuid, userEmail, previousFlow }) => {
  const params = removeDuplicateQueryParams(location.search)

  const flowToResume = params.flow || previousFlow

  const resume = () => {
    setCookies(
      {
        identityId: userUuid,
        token: refreshCookie,
      },
      userEmail,
    )

    backend.defaults.headers.Authorization = `token ${refreshCookie}`
    setResumeFlow(true)
    setFlow(flowToResume)

    window.analytics.identify(userUuid, {
      app_version: APP_VERSION,
      partner: null,
      funnel_type: flowToResume,
      tier_price: tierPrice,
    })

    Sentry.configureScope(scope => {
      scope.setUser({ id: userUuid })
    })
    setShowWelcomeModal(true)
  }

  if (!optimizely.isFeatureEnabled(registrationServiceResumeFunnel)) {
    return resume()
  }

  return updateAttribution(metadata.name).then(() => resume())
}

const handleError = ({ setLoading, flow }) => err => {
  setResumeFlow(false)
  sendToSentry(err, {
    breadcrumb: `Attempting to resume ${flow} flow`,
  })
  setLoading(false)
}

const handleContinue = ({
  route,
  optimizely,
  isDynamicFunnelEnabled,
  routeToPage,
  metadata,
  updateAttribution,
}) => () => {
  const isMocFunnelEnabled = optimizely.isFeatureEnabled(mocOnboardingEnabled)
  if (isDynamicFunnelEnabled) {
    // This is a workaround until we bring experimentation into registration service
    // Update the user's funnel to the default funnel if the MOC onboarding flow is toggled off
    if (!isMocFunnelEnabled && metadata.name === FunnelName.MightyOakCard) {
      updateAttribution(FunnelName.Default).then(() => {
        return routeToPage(PageName.Signup, ActionType.Continue)
      })
    }

    return routeToPage(PageName.Signup, ActionType.Continue)
  }

  route({ from: FlowRoute.Signup, action: isMocFunnelEnabled ? 'moc-onboarding' : 'continue' })
}

type HandlerProps = {
  fetchRefreshCookie: () => any
  fetchUser: () => any
  handleRedirectToTierSelection: () => any
  handleError: () => any
  handleRegistrationComplete: () => any
  handleResume: (ctx: any) => any
  handleSkipResume: () => any
  handleContinue: () => void
  handleResumeCases: (ctx: any) => void
  setIsRegistrationComplete: () => void
}

export type WithResumeFlowProps = {
  newTab: boolean
  didLogin: boolean
  loading: boolean
  setLoading: (loading: boolean) => void
  showWelcomeModal: boolean
  setShowWelcomeModal: (showWelcomeModal: boolean) => void
  isRegistrationFlow: boolean
  setIsTieredRegEnabled: () => void
  isTieredRegEnabled
  tierPriceId: string
  registrationComplete: boolean
} & HandlerProps &
  WithOptimizelyProps &
  WithTieredRegistrationProps

const setResumeFlow = (isResuming = false) => {
  window.sessionStorage.setItem(IS_RESUME_FLOW, isResuming.toString().toLowerCase())
}

export default compose(
  fromRenderProps(
    FunnelContext.Consumer,
    ({
      registrationFunnel,
      metadata,
      updateAttribution,
      isDynamicFunnelEnabled,
      routeToPage,
    }: FunnelContextProps) => ({
      registrationFunnel,
      metadata,
      updateAttribution,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  withEnv,
  withState('loading', 'setLoading', true),
  withBackend,
  withApollo,
  withFlowRouter,
  withRouter,
  withOptimizely,
  withTieredRegistration,
  withState('loading', 'setLoading', true),
  withState('showWelcomeModal', 'setShowWelcomeModal', false),
  withState('registrationComplete', 'setRegistrationComplete', false),
  withProps(({ env, isRegistrationFlow }) => {
    return {
      didLogin: didLogin(document.referrer, env),
      newTab: isNewTab(),
      isRegistrationFlow,
    }
  }),
  withHandlers<{}, {}>({
    fetchRefreshCookie,
    fetchUser,
    handleError,
    handleRedirectToTierSelection,
    handleRegistrationComplete,
    handleResume,
    handleSkipResume,
    handleContinue,
  }),
  withHandlers<{}, {}>({
    handleResumeCases,
  }),
  branch(({ loading }) => loading, renderComponent(ResumeFlow)),
)
